import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import {
  PlannerBlock, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { cuisinesForSuburb, blogsForSuburb } from "../data/internalLinks";
import { SUBURB_BY_SLUG, SUBURBS, REGIONS } from "../data/suburbs";

const FAQS_FOR = (name) => ([
  { q: `Do you charge travel fees for ${name}?`, a: `No — ${name} is inside our Greater Melbourne coverage zone. The shoot-day rate is the same whether we're in the CBD or out at ${name}. No travel fees, no minimums.` },
  { q: `How quickly can you shoot in ${name}?`, a: `Most ${name} shoots are booked 7–14 days ahead. Urgent (48–72 hour) turnarounds are usually possible — call directly and we'll confirm the same day.` },
  { q: `Do you understand the local dining scene in ${name}?`, a: `Yes — we shoot in ${name} regularly. We brief into the local context (cuisine mix, average price point, delivery vs dine-in split) before the call, so the photography matches what the suburb actually rewards.` },
  { q: `Can you handle both menu and venue photography in ${name}?`, a: `Yes — every shoot can combine menu, venue, GBP and reels in one day if scoped properly. Most ${name} briefs are multi-channel.` },
]);

export default function SuburbPage() {
  const { suburb } = useParams();
  const data = SUBURB_BY_SLUG[suburb];
  if (!data) return <Navigate to="/locations" replace />;

  const region = REGIONS.find((r) => r.id === data.region);
  const neighbours = SUBURBS.filter((s) => s.region === data.region && s.slug !== data.slug).slice(0, 6);
  const faqs = FAQS_FOR(data.name);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />

      {/* Hero */}
      <section className="relative min-h-[80svh] overflow-hidden bg-ink">
        <div className="absolute inset-0">
          <Photo slug={data.hero} alt={`Restaurant photographer ${data.name}`} priority ratio="auto" className="!relative h-full w-full" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/45 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[80svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
          <Breadcrumb current={`${data.name} · Restaurant Photographer`} />
          <div className="flex-1 flex flex-col justify-center max-w-5xl">
            <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
              Coverage · {region?.label || "Melbourne"} · No travel fees
            </p>
            <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
              Restaurant photographer
              <br />
              <span className="italic font-light text-ember">{data.name}.</span>
            </h1>
            <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
              {data.context}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/get-a-quote"><CTAButton variant="primary" size="lg">Get a {data.name} Quote</CTAButton></Link>
              <a href="tel:0494756111"><CTAButton variant="ghost" size="lg" arrow={false}>Call 0494 756 111</CTAButton></a>
            </div>
          </div>
        </div>
      </section>

      {/* Dining scene */}
      <section className="relative bg-ink text-bone py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-12 items-end">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">Local dining scene</p>
              <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
                What we shoot most in
                <br />
                <span className="italic font-light text-ember">{data.name}.</span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:pb-3">
              <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
                We brief into the local context before the shoot — so the photography matches what {data.name} actually rewards on Google, Uber Eats and your website.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-bone/10 border border-bone/10">
            {data.dining.map((d, i) => (
              <div key={i} className="bg-ink p-7 md:p-8">
                <span className="font-display italic text-2xl text-ember leading-none">0{i + 1}</span>
                <p className="mt-4 font-display text-lg md:text-xl tracking-editorial leading-tight">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PlannerBlock
        eyebrow={`Pricing · ${data.name} restaurants`}
        headline="Scope your shoot"
        italic={`for ${data.name}.`}
        blurb={`The same planner we use for every Melbourne suburb. Per-image rates tier down as the menu grows — and ${data.name} is inside our no-travel-fee coverage zone.`}
      />

      {/* Neighbour suburbs */}
      {neighbours.length > 0 && (
        <section className="bg-bone text-ink py-20 lg:py-28">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">Nearby coverage</p>
            <h2 className="font-display tracking-editorial leading-[0.95] mb-10" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
              We also shoot
              <span className="italic font-light text-ember"> {region?.label || "Melbourne"}.</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-ink/10 border border-ink/10">
              {neighbours.map((n) => (
                <Link
                  key={n.slug}
                  to={`/restaurant-photographer/${n.slug}`}
                  data-testid={`neighbour-${n.slug}`}
                  className="bg-bone hover:bg-ink hover:text-bone transition-colors p-5"
                >
                  <p className="font-display text-base md:text-lg tracking-editorial leading-tight">{n.name}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wider2 font-bold opacity-50">{region?.label}</p>
                </Link>
              ))}
            </div>
            <div className="mt-10">
              <Link to="/locations"><CTAButton variant="secondary" size="md">View all Melbourne suburbs</CTAButton></Link>
            </div>
          </div>
        </section>
      )}

      <SisterServices keys={["menu", "ubereats", "cafe", "gbp"]} />
      <InternalRail
        eyebrow={`Cuisine specialists · ${data.name}`}
        headline="The cuisines we shoot most in"
        italic={`${data.name}.`}
        theme="bone"
        cols={4}
        items={cuisinesForSuburb(data.slug)}
      />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More from"
        italic="the journal."
        theme="ink"
        cols={3}
        items={blogsForSuburb(data.slug)}
      />
      <FAQSection
        eyebrow={`Common questions · ${data.name}`}
        headline="Coverage, travel,"
        italic="and what we shoot here."
        intro={`Quick answers for restaurants in ${data.name}.`}
        faqs={faqs}
      />
      <FinalCTA
        eyebrow={`Final step · ${data.name} restaurants`}
        headline={`Ready to shoot in`}
        italic={`${data.name}?`}
        intro={`Most ${data.name} briefs are quoted in under five minutes by phone. No travel fees, no minimums.`}
        backdropSlug={data.hero}
      />
      <Footer />
    </div>
  );
}
